import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Quote, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileUpload } from "@/components/site/FileUpload";
import { SectionHead } from "@/components/home/HomeSections";
import { api, errMsg, publicFileUrl } from "@/lib/api";

const Stars = ({ n, size = "h-4 w-4", onPick }) => (
  <div className="flex text-amber-400">
    {[1, 2, 3, 4, 5].map((i) => (
      <button type="button" key={i} disabled={!onPick} onClick={() => onPick?.(i)} aria-label={`${i} star`} data-testid={onPick ? `review-star-${i}` : undefined}>
        <Star className={`${size} ${i <= n ? "fill-current" : "text-muted-foreground/30"}`} />
      </button>
    ))}
  </div>
);

const ReviewForm = ({ onDone }) => {
  const [f, setF] = useState({ name: "", city: "", service: "", rating: 5, text: "", photo: null });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { ...f, photo: f.photo?.path || null });
      toast.success("Thank you! Your review will appear after verification.");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="review-form">
      <Stars n={f.rating} size="h-7 w-7" onPick={(rating) => setF({ ...f, rating })} />
      <div className="grid grid-cols-2 gap-3">
        <Input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="review-name-input" />
        <Input placeholder="City" value={f.city} onChange={set("city")} data-testid="review-city-input" />
      </div>
      <Input placeholder="Service availed (e.g. AC Repair)" value={f.service} onChange={set("service")} data-testid="review-service-input" />
      <Textarea required minLength={10} placeholder="Share your experience" value={f.text} onChange={set("text")} data-testid="review-text-input" />
      <FileUpload kind="review" label="Add your photo or a service photo (optional)" accept="image/jpeg,image/png,image/webp" value={f.photo} onChange={(photo) => setF({ ...f, photo })} testid="review-photo-upload" />
      <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="review-submit-button">{busy ? "Submitting…" : "Submit Review"}</Button>
    </form>
  );
};

export const Reviews = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: reviews = [], isLoading } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data });
  const action = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-primary/40 text-primary" data-testid="write-review-button"><MessageSquarePlus className="h-4 w-4 mr-2" />Rate your experience</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-2xl">
        <DialogHeader><DialogTitle>Rate your Onkaal experience</DialogTitle></DialogHeader>
        <ReviewForm onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["reviews"] }); }} />
      </DialogContent>
    </Dialog>
  );
  return (
    <section className="container-x section-y" data-testid="reviews-section">
      <SectionHead eyebrow="Customer Reviews" title="What our customers say" sub="Genuine, verified reviews from Onkaal customers." action={action} />
      {!isLoading && reviews.length === 0 && (
        <div className="card-soft p-10 text-center" data-testid="reviews-empty">
          <Quote className="h-10 w-10 mx-auto text-primary/40" />
          <p className="mt-3 font-semibold">Verified customer reviews appear here</p>
          <p className="text-sm text-muted-foreground mt-1">Every review comes from a real Onkaal customer, with their rating, city and service booked. Booked with us? Tap "Rate your experience".</p>
        </div>
      )}
      {reviews.length > 0 && (
        <Carousel opts={{ align: "start", loop: reviews.length > 3 }} className="px-1" data-testid="reviews-slider">
          <CarouselContent>
            {reviews.map((r) => (
              <CarouselItem key={r.id} className="md:basis-1/2 lg:basis-1/3">
                <article className="card-soft p-6 h-full flex flex-col gap-4" data-testid={`review-card-${r.id}`}>
                  <Stars n={r.rating} />
                  <p className="text-sm leading-relaxed flex-1">"{r.text}"</p>
                  {r.photo && <img src={publicFileUrl(r.photo)} alt={`Service photo by ${r.name}`} loading="lazy" className="h-40 w-full object-cover rounded-xl" />}
                  <div className="flex items-center gap-3">
                    {r.photo ? <img src={publicFileUrl(r.photo)} alt={r.name} className="h-10 w-10 rounded-full object-cover" /> : <span className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-semibold">{r.name[0]?.toUpperCase()}</span>}
                    <div><p className="font-semibold text-sm">{r.name}</p><p className="text-xs text-muted-foreground">{[r.service, r.city].filter(Boolean).join(" · ")}</p></div>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:flex -left-4" data-testid="reviews-prev" />
          <CarouselNext className="hidden sm:flex -right-4" data-testid="reviews-next" />
        </Carousel>
      )}
    </section>
  );
};
